import { actionSetErrorMessage, Localization, Logger, PersistenceObjectType, PersistenceScope, SettingsFixedPriorities, SettingsManager, ViamapPersistenceLayer } from "@viamap/viamap2-common";
import { Feature } from "src/states/ApplicationStateFeatures";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { closeWindow, ManagedWindow, WindowId } from "src/WindowManager/WindowState";
import { GroupInfo, SettingsEditor, SingleSetting } from "./SettingsEditor";
import { CatchmentInterface } from "src/managers/CatchmentInterface";
import { SERVITUT_PDF_NAMING_SETTING_KEY } from "src/managers/ServitutDownloadNaming";
import { DEFAULT_ASSUMPTIONS } from "src/modules/JPBModule/JPBState";

export function UserCustomerSettingsEditor(props: { scope: PersistenceScope, managedWindow: ManagedWindow }): JSX.Element | null {
   const { dispatch: windowDispath } = useWindowContext()

   return (
      <SettingsEditor
         scope={props.scope}
         settingsManager={SettingsManager}
         priority={PersistenceScopeToSettingsFixedPriorities(props.scope)}
         managedWindow={props.managedWindow}
         getSource={(key: string) => SettingsManager.getSystemSettingSource(key)?.priority || 0}
         allSettings={Settings}

         affectedMessageText={props.scope === PersistenceScope.User ? Localization.getText("Changed settings affect current user") : Localization.getText("Changed settings affect all employees from the same organization")}
         filteredSettingsGroup={props.managedWindow.windowParams?.filter ? SettingsGroups.filter(props.managedWindow.windowParams?.filter) : SettingsGroups}
         callbackOnClose={() => windowDispath(closeWindow(props.managedWindow.id))}
         callbackToResetCatchment={() => {
            CatchmentInterface.catchmentGeoJSONCache = {};
            throw new Error("Not implemented");
         }}
         callbackOnSaveChanges={(settings, sessionState, onClose, appMessageDispatch, source: SettingsFixedPriorities) => {
            let scope = PersistenceScope.User
            switch (source) {
               case SettingsFixedPriorities.CustomerSettings:
                  scope = PersistenceScope.Customer
                  break;
               case SettingsFixedPriorities.UserSettings:
                  scope = PersistenceScope.User
                  break;
               default:
                  return appMessageDispatch(actionSetErrorMessage(Localization.getText("Unknown location for settings")));
            }

            let inst = new ViamapPersistenceLayer(SettingsManager.getSystemSetting("viamapStoreS3Bucket"));
            let objectRef = inst.createJSONObjectRef(PersistenceObjectType.Settings, "Mapit");
            onClose();
            let metaData = {
               filename: encodeURIComponent(objectRef),
               lastModified: new Date().getTime().toString()
            };
            inst.uploadObject(scope, PersistenceObjectType.Settings, sessionState.customerRef, sessionState.userRef, JSON.stringify(settings), objectRef, metaData)
               .catch(error => {
                  appMessageDispatch(actionSetErrorMessage(Localization.getText("Could not save settings")));
                  Logger.logError("SettingsEditor", "onClickSaveCustomUserSettings", error);
               });
         }}
      />
   )
}


/**
* warning: key musn't be changed used in external components
*/
const SettingsGroups: GroupInfo = [
   {
      key: "mapit",
      da: "Mapit",
      en: "Mapit",
   },
   {
      key: "catchment",
      da: "Rejsetid",
      en: "Traveltime"
   },
   {
      key: "plot",
      da: "Plot opsætning",
      en: "Plot defaults"
   },
   {
      key: "bnbo",
      da: "BNBO",
      en: "BNBO"
   },
   {
      key: "jpb",
      da: "Jordprisberegner",
      en: "Land Price Calculator"
   },
   {
      key: "propertyInfo",
      da: "Ejendomsinfo",
      en: "Property Info"
   },
   {
      key: "debugging",
      da: "Fejlfinding",
      en: "Debugging"
   }
]

const Settings: SingleSetting[] = [
   {
      access: Feature.BasicMap,
      group: "mapit",
      key: "AsDefaultCreatePopupInfoForAllFieldsInGeoJSONImports",
      type: "Boolean",
      default: true,
      true: { da: "Alle Attributter i Popup", en: "All attrib. in Popup" },
      false: { da: "Ingen Attibutter i Popup", en: "No attrib. in Popup" },
      da: "Json Import",
      en: "Json Import",
   },
   {
      access: Feature.PropertyInfo,
      group: "mapit",
      key: "showPropertyInfoOnClick",
      type: "Boolean",
      default: true,
      true: { da: "Vis ejendomsoplysninger", en: "Show property info" },
      false: { da: "Vis ikke ejendomsoplysninger", en: "Do not show property info" },
      da: "Vis ejendomsoplysninger ved klik",
      en: "Show property info on click",
   },
   {
      access: Feature.BasicMap,
      group: "mapit",
      key: "OrderBackgroundLayersInDrawingOrder",
      default: true,
      da: "Rækkefølge af Baggrundslag",
      en: "Order of Background layers",
      type: "Boolean",
      true: { da: "Som tegnet på kortet", en: "As shown on map" },
      false: { da: "Som vist på listen", en: "As ordered in layer list" }
   },
   {
      access: Feature.BasicMap,
      group: "mapit",
      key: "SaveOpendedFeatureLayers",
      default: false,
      da: "Husk tændte lag",
      en: "Remeber active layer",
      type: "Boolean",
      true: { da: "Slået til", en: "Activated" },
      false: { da: "Slået fra", en: "Deactivated" }
   },
   {
      access: Feature.PowerUser,
      group: "mapit",
      key: "timeBetweenLoadingMultipleFilesMS",
      type: "Integer",
      suffix: "ms",
      default: true,
      min: 0,
      max: 2000,
      da: "Tid mellem fil indlæsning",
      en: "Time between reading imported files",
   },
   {
      access: Feature.TravelTimeMultiple,
      group: "catchment",
      key: "catchmentStateDefaults.reverse",
      type: "Boolean",
      true: { da: "Mod Punkt", en: "Towards Points" },
      false: { da: "Fra Punkt", en: "From Points" },
      da: "Retning",
      en: "Direction",
   },
   {
      access: Feature.TravelTimeMultiple,
      group: "catchment",
      key: "catchmentStateDefaults.duration",
      type: "Integer",
      default: 90,
      suffix: "min",
      min: 5,
      max: 180,
      da: "Max rejsetid",
      en: "Max travel duration",
   },
   {
      access: Feature.TravelTimeMultiple,
      group: "catchment",
      key: "catchmentStateDefaults.date",
      default: 20230810,
      type: "Date",
      da: "Dato",
      en: "Date"
   },
   {
      access: Feature.TravelTimeMultiple,
      group: "catchment",
      key: "catchmentStateDefaults.time",
      type: "TimeADay",
      da: "Tidspunkt",
      en: "Travel time"
   },
   {
      access: Feature.TravelTimeMultiple,
      group: "catchment",
      key: "catchmentStateDefaults.earliestArrivalTime",
      default: 0,
      type: "TimeADay",
      da: "Tidligst ank./afr.",
      en: "Earliest arr./dep.",
   },
   {
      access: Feature.TravelTimeMultiple,
      group: "catchment",
      key: "catchmentStateDefaults.earliestArrival",
      default: true,
      type: "Boolean",
      true: { da: "Kortest rejsetid", en: "Shortest traveltime" },
      false: { da: "Kortest ventetid", en: "Shortest waiting time" },
      da: "Beregningsmetode",
      en: "Calculation method"
   },
   {
      access: Feature.TravelTimeMultipleBufferSetting,
      group: "catchment",
      key: "catchmentStateDefaults.buffer",
      default: 0.002,
      precision: 0.0001,
      type: "Float",
      min: 0,
      max: 0.002,
      da: "Rejsetids Buffer",
      en: "Traveltime Buffer"
   },
   {
      access: Feature.MapPlot,
      group: "plot",
      key: "plot.remember",
      type: "Boolean",
      default: true,
      true: { da: "Husk plot opsætning", en: "Remember plot settings" },
      false: { da: "Glem plot opsætning", en: "Do not remember plot settings" },
      da: "Husk opsætning",
      en: "Remember settings",
   },
   {
      access: Feature.MapPlot,
      group: "plot",
      key: "plot.title",
      type: "String",
      default: "Default Map Title",
      da: "Plot Titel",
      en: "Plot Title",
   },
   {
      access: Feature.MapPlot,
      group: "plot",
      key: "plot.description",
      type: "TextArea",
      default: "Default Description",
      da: "Plot Beskrivelse",
      en: "Plot Description"
   },
   {
      access: Feature.MapPlot,
      group: "plot",
      key: "plot.includeDate",
      type: "Boolean",
      default: true,
      true: { da: "Inkluder dato", en: "Include date" },
      false: { da: "Inkluder ikke dato", en: "Do not include date" },
      da: "Inkluder dato i plot",
      en: "Include date in plot"
   },
   {
      access: Feature.MapPlotLogo,
      group: "plot",
      key: "plot.image",
      type: "ExternalEditor",
      default: {
         "url": "default",
         "desiredHeight": 150,
         "offsetX": 10,
         "offsetY": 10
      },
      da: "Plot Logo",
      en: "Plot Logo",
      windowId: WindowId.MapPlotLogoEditor,
   },
   // NOTE: `jpb.assumptions.agricultureFactor` is intentionally NOT exposed in the UI - the
   // calculation continues to read it from DEFAULT_ASSUMPTIONS (1.0) so consumers stay valid,
   // but users should not be able to change it.
   {
      access: Feature.JPBTool,
      group: "jpb",
      key: "jpb.assumptions.nonAgricultureFactor",
      type: "Percent",
      min: 0,
      max: 100,
      precision: 1,
      default: 0.5,
      suffix: "%",
      da: "Værdi af andet end marker i forhold til markværdi",
      en: "Value of non-field areas relative to field value",
   },
   {
      access: Feature.JPBTool,
      group: "jpb",
      key: "jpb.assumptions.areaMin",
      type: "Hectare",
      min: 0,
      max: 10000,
      precision: 0.1,
      default: DEFAULT_ASSUMPTIONS.areaMin,
      suffix: "ha",
      da: "Minimumsareal for handler",
      en: "Minimum transaction area",
   },
   {
      access: Feature.JPBTool,
      group: "jpb",
      key: "jpb.assumptions.areaMax",
      type: "Hectare",
      min: 0,
      max: 10000,
      precision: 0.1,
      default: DEFAULT_ASSUMPTIONS.areaMax,
      suffix: "ha",
      da: "Maksimumsareal for handler",
      en: "Maximum transaction area",
   },
   {
      access: Feature.JPBTool,
      group: "jpb",
      key: "jpb.assumptions.effectiveMarkOverlapThreshold",
      type: "Integer",
      min: 0,
      max: 100,
      default: DEFAULT_ASSUMPTIONS.effectiveMarkOverlapThreshold,
      suffix: "%",
      da: "Minimum markprocent for handler",
      en: "Minimum farmland percentage for transactions",
   },
   {
      access: Feature.JPBTool,
      group: "jpb",
      key: "jpb.assumptions.soilQualityFactors.JB_1_2",
      type: "Percent",
      min: 0,
      max: 100,
      precision: 1,
      default: 0.75,
      da: "Jordbundsfaktor JB 1-2",
      en: "Soil factor JB 1-2",
      tooltip: {
         da: "Grovsandet jord, Finsandet jord",
         en: "Coarse sandy soil, Fine sandy soil",
      },
   },
   {
      access: Feature.JPBTool,
      group: "jpb",
      key: "jpb.assumptions.soilQualityFactors.JB_3_4",
      type: "Percent",
      min: 0,
      max: 100,
      precision: 1,
      default: 0.925,
      da: "Jordbundsfaktor JB 3-4",
      en: "Soil factor JB 3-4",
      tooltip: {
         da: "Grov lerblandet sandjord, Fin lerblandet sandjord",
         en: "Coarse clay-mixed sandy soil, Fine clay-mixed sandy soil",
      },
   },
   {
      access: Feature.JPBTool,
      group: "jpb",
      key: "jpb.assumptions.soilQualityFactors.JB_5_6",
      type: "Percent",
      min: 0,
      max: 100,
      precision: 1,
      default: 1,
      da: "Jordbundsfaktor JB 5-6",
      en: "Soil factor JB 5-6",
      tooltip: {
         da: "Grov sandblandet lerjord, Fin sandblandet lerjord",
         en: "Coarse sand-mixed clay soil, Fine sand-mixed clay soil",
      },
   },
   {
      access: Feature.JPBTool,
      group: "jpb",
      key: "jpb.assumptions.soilQualityFactors.JB_7_10",
      type: "Percent",
      min: 0,
      max: 100,
      precision: 1,
      default: 0.85,
      da: "Jordbundsfaktor JB 7-10",
      en: "Soil factor JB 7-10",
      tooltip: {
         da: "Lerjord, Svær lerjord, Meget svær lerjord, Siltjord",
         en: "Clay soil, Heavy clay soil, Very heavy clay soil, Silt soil",
      },
   },
   {
      access: Feature.JPBTool,
      group: "jpb",
      key: "jpb.assumptions.soilQualityFactors.JB_11",
      type: "Percent",
      min: 0,
      max: 100,
      precision: 1,
      default: 0.6,
      da: "Jordbundsfaktor JB 11",
      en: "Soil factor JB 11",
      tooltip: {
         da: "Humusjord",
         en: "Humus soil",
      },
   },
   {
      access: Feature.JPBTool,
      group: "jpb",
      key: "jpb.assumptions.buildingAreaOkM2",
      type: "Integer",
      min: 0,
      max: 100000,
      default: 50,
      suffix: "ha",
      da: "Handler, hvor der indgår bygninger og hvor det handlede areal er under...",
      en: "Transactions including buildings where the traded area is below...",
   },
   {
      access: Feature.PropertyInfo,
      group: "propertyInfo",
      key: "propertyInfoMarkIntersectBuffer",
      type: "Integer",
      default: -5,
      min: -25,
      max: 25,
      suffix: "m",
      da: "Afstand for check af markers overlap med ejendom",
      en: "Distance to check overlaps between property and fields",
   },
   {
      access: Feature.PropertyInfo,
      scope: PersistenceScope.User,
      group: "mapit",
      key: SERVITUT_PDF_NAMING_SETTING_KEY,
      type: "Select",
      default: "standard",
      da: "Filnavngivning af servitutter",
      en: "Servitut naming",
      options: [
         {
            value: "standard",
            da: "Standard (ddmmåååå)",
            en: "Standard (ddmmyyyy)",
         },
         {
            value: "yearBased",
            da: "Årstalsbaseret (ååååmmdd)",
            en: "Year based (yyyymmdd)",
         },
         {
            value: "priority",
            da: "Prioritet + løbenr",
            en: "Priority + serial no.",
         },
         {
            value: "priorityWithHeading",
            da: "Prioritet + overskrift",
            en: "Priority + heading",
         }
      ]
   },
   {
      access: Feature.Debugging,
      scope: PersistenceScope.User,
      group: "debugging",
      key: "debuggingShowExtraDebugging",
      default: false,
      type: "Boolean",
      da: "Vis ekstra debugging knapper",
      en: "Show extra debugging buttons",
      true: { da: "Vis knapper", en: "show buttons" },
      false: { da: "Skjul knapper", en: "hide buttons" }
   },
   {
      access: Feature.Debugging,
      scope: PersistenceScope.User,
      group: "debugging",
      key: "debuggingShowExtraDebuggingPlot",
      default: false,
      type: "Boolean",
      da: "Vis ekstra debugging plot",
      en: "Show extra debugging plot",
      true: { da: "Vis ekstra", en: "show extra" },
      false: { da: "Skjul ekstra", en: "hide extra" }
   },
   {
      access: Feature.Debugging,
      scope: PersistenceScope.User,
      group: "debugging",
      key: "MakeTopOnOpen",
      default: false,
      type: "Boolean",
      da: "Fokus på åbning af allerede åbne vinduer",
      en: "Focus on opening already open windows",
      true: { da: "Fokus på åbning", en: "Focus on opening" },
      false: { da: "Ingen fokus", en: "No focus" }
   }
]


function PersistenceScopeToSettingsFixedPriorities(scope: PersistenceScope) {
   switch (scope) {
      case PersistenceScope.User: return 60
      case PersistenceScope.Customer: return 50
      default: return 60
   }
}
